import qc from 'qc'
import { scripts } from '@ps/bridge/qc/scripts/index'
import _ from 'lodash'
import Vue from 'vue'
import { referenceLineSet } from '@ps/types/workbench/stage.type'
import {
  getVpAstActionConfig,
  getVpConditionConf,
} from '@ps/bridge/qc/vp/config'
import { LAYOUT_RESOLUTION } from '@ps/bridge/qc/scripts/layout'
import {
  BODYTYPE,
  DEFAULT_BOX2D_PRECISION,
} from '@ps/workbench/undo-redo/module/box2d'
import { checkIsSupportBox2d } from '@ps/store/workbench/visual-program/utils'
import { IvTrack } from '@ps/store/interactive-video/timeline/types'
import { IvBrain } from '@ps/store/interactive-video/brain/types'
export interface AstActionParam {
  jumpSceneId?: string
  initData?: {
    [key: string]: any
  }
  type?: string
  properties?: {
    [key: string]: {
      component?: string
      title?: string
      field?: {
        [key: string]: any
      }
    }
  }

  [key: string]: any
}

export type AstActionItem = {
  category?: string
  id: string // action 类型 id，如果同一种类型，则 id 一样
  uuid: string // action id，每个 action 都不一样（有一些旧数据没有这个数据）
  node: string // 响应对象节点 id
  method: string // 回调函数名称，如 vpPosition
  script: string // 脚本名称，如果响应事件回调依赖脚本的话就需要配置 script
  target: boolean // 是否有响应对象
  param?: AstActionParam // 事件回调函数参数
  properties?: {
    component?: string
    title?: string
  } // 编辑器 UI 相关
  paramLabel?: string // 编辑器 UI 相关
  paramLabelUI?: string // 编辑器 UI 相关
  targetLabel: string // 响应对象在编辑器上的展示文案
  label: string // action 在编辑器上的展示文案
  targetList?: string[] // action的多选响应对象
  paramParseFunc?: (
    param: any,
    actionI18nConfig: any,
    sceneNode?: qc.Node | null,
    lang?: string,
  ) => string // 加工 paramLabelUI 的函数，保存响应事件时调用
}

export type AstConditionItemExt = {
  dragTargetArea?: {
    [uuid: string]: string[]
  }
  dragBound?: string
}

type compareData = {
  data: string // vpGamePlay中的全局变量
  targetNode: string // gamePlay节点
  targetNodeScript: string // gamePlay节点上的脚本ps.VPGamePlay
}

export type paramsItem = {
  compareData1: compareData // 全局变量的被比较量
  compareData2: compareData | string | number | boolean // 全局变量的比较量
  compareData2Type: string // 全局变量比较量的类型 globalVar | string | number | boolean
  compareMethod: string // 全局变量的比较符 = > >= < <= !=
}
// 擦除组件参数
type eraseParam = {
  customEventId: string | null // 自定义判定事件id
  step: string | null // 阶段数值
}
export type AstConditionItem = {
  node: string // 节点 id
  type: string // 类型，暂时看起来固定是 event
  conditionLabel: string // 在编辑器上展示的文案
  content?: string // 触发条件
  node2?: string
  ext?: AstConditionItemExt
  params?: paramsItem
  erase?: eraseParam // 擦除组件参数
  dragResetPos?: boolean // 拖拽松手后是否复位
}

export type AstEventItem = {
  action: AstActionItem[] // 事件行为
  disabled: boolean
  condition: AstConditionItem[] // 事件条件
  id: string // 事件 id
  eventName?: string // 自定义事件名字
  eventNameEn?: string // 自定义事件英文名字
  once?: boolean // 只生效一次的纯全局变量条件判断
}

export type QcWorldPosition = {
  x: number
  y: number
  localX?: number // 基于父节点的 x 坐标
  localY?: number // 基于父节点的 y 坐标
}

export interface EditorDataScript {
  // 当前节点约束的状态值，用于视图显示
  screenAdapter: {
    [key: string]: {
      horizontal: string
      vertical: string
    }
  }
  // 屏幕约束的开关，默认都是开的
  screenAdapterSwitch: boolean
  languageConfig: {
    langList: string[]
    defaultLang: string
  }
  // $data 是动态配置，如果仅仅是要新增属性的话，添加到 $data 中，就不需要修改脚本
  $data: {
    nodeType: string // 节点类型
    ratioLock: boolean // 是否锁定缩放尺寸比例
    size: number // 文件大小
    duration: number // 文件时长
    worldPosition: {
      // 横竖屏世界坐标（改为布尔值，代表是否在新版 ps 中重新生成 layout
      pdef?: boolean
      ldef?: boolean
    }
    filters: {
      hue: number
      saturation: number
      luminance: number
      blur: number
    }
    referenceLine?: {
      pdef?: referenceLineSet[] // 场景内的参考线, 竖屏参考线
      ldef?: referenceLineSet[] // 场景内的参考线, 横屏参考线
    }
    nodeOrigin: number // 节点来源，当 origin 为 1 时，说明导入基础包时就自带的节点，即不是从 ps 编辑器中新增的节点， 为 2 时，代表是新增的节点
    // 是否为临时节点
    // 如从 pt 转 ps 时，没有关联文本节点的文本配置，需要新增一个节点，保证在 ps 项目下可以编辑该文本配置
    // 为什么使用新增节点的方式去做这个需求，因为这样可以保证逻辑一致
    // 一开始做的时候，在保存时会删掉临时节点，但是后来发现不能删除临时节点。因为删除之后，不刷新的情况下，再次保存就会导致临时节点对应的文本内容丢失掉（因为在上一次保存时，已经将临时节点删除了）
    isTempNode?: boolean
    oldLangKey?: string // 使用旧脚本的包转换为 ps 自由制作的版本时需要存储该节点原来的 langKey
    dragLinkageNodeUUID?: string | boolean // 拖拽联动的 uuid
    isTransformByPt?: boolean // 判断该素材是否是从 PT 模版转过来，当前只会挂载在 UIRoot 节点上
    isAdapted?: boolean // 是否是适配过的非标准包
    unSplitProp?: {
      // 取消横竖屏拆分属性值，当前支持 visible、adaptation、pivot、scale、fontSize、dragonbone
      [key: string]: boolean
    }
    drawLineData?: string // 连线配对数据
    box2d?: {
      // box2d数据
      bodyType?: number // 刚体类型
    }
    oldLayoutData?: {
      // 自研项目中，如果设置到精简 layout 数据，需要把原来的 layout 数据存储起来，用于恢复
      x?: number
      y?: number
      width?: number
      height?: number
    }
    layerColor?: number // 图层颜色
    timeline?: IvTrack[] // 互动视频时间轴数据
    brain?: IvBrain // 互动视频脑图数据
  }
  sequenceFrameType: string
  vpAst: {
    [key: string]: AstEventItem
  }
}

export interface SwitchSceneEventData {
  sceneId: string // 节点 id 对应的场景 id
  eventId: string // 事件 id
  actionId: string // 事件动作 id
  nodeId: string // 节点 id
  switchSceneId: string // 跳转的目标场景 id
}

export const nodeTypes = {
  scene: 'scene', // 场景节点
  group: 'group', // 组节点
  quickLayout: 'quickLayout', // 快捷布局
  tweenPositionArea: 'tweenPositionArea', // 结束位置虚拟节点
  drawLine: 'drawLine', // 连线组件节点
  drawLineArea: 'drawLineArea', // 连线组件里的起止区域
  erase: 'erase', // 擦除组件节点
  customJudgeAreas: 'customJudgeAreas', // 擦除组件自定义判定区域节点
  park: 'park', // 停车组件
  mask: 'mask', // 遮罩组件节点
  puzzle: 'puzzle', // 拼图节点
  carousel: 'carousel', // 轮播组件节点
  carouselGroup: 'carousel-group', // 轮播组件组节点
  carouselRes: 'carouselRes', // 轮播组件资源组节点
  carouselPite: 'carouselPite', // 轮播组件坑位组节点
  carouselShadow: 'carouselShadow', // 轮播组件遮罩节点
  shelves: 'shelves', // 货架三消节点
  ivHotArea: 'ivHotArea', // 互动视频热区节点
  ivEndingInstall: 'ivEndingInstall', // 互动视频结束页跳转商店节点
  ivGroup: 'ivGroup', // 互动视频编辑器的组
}

export const nodeOrigin = {
  // 导入基础包时就自带的节点，即不是从 ps 编辑器中新增的节点
  import: 1,
  local: 2, // 代表是从 PS 编辑器中新增的节点
}

const initFilter = (config: EditorDataScript) => {
  if (_.isEmpty(config.$data.filters)) {
    Vue.set(config.$data, 'filters', {
      hue: 0,
      saturation: 0,
      luminance: 0,
      blur: 0,
    })
  }
}
const initBox2d = (config: EditorDataScript, node: qc.Node) => {
  if (_.isEmpty(config.$data.box2d)) {
    let bodyType = BODYTYPE.NOTRIGID // 默认是非刚体
    let precision = DEFAULT_BOX2D_PRECISION // 默认精度3
    if (checkIsSupportBox2d()) {
      const box2dScript = node.getScript('qc.Box2D.Body')
      if (box2dScript) {
        bodyType = box2dScript.type
        precision = box2dScript.precision
      }
      Vue.set(config.$data, 'box2d', {
        bodyType,
        precision,
      })
    }
  }
}
const reactive = (config: EditorDataScript) => {
  Vue.set(config, '$data', config.$data)
}
export const getScript = (
  node: qc.Node | qc.Dom,
  addScriptIfScriptNotExist = true,
): EditorDataScript | undefined => {
  const config: EditorDataScript = node.getScript(scripts.editor.name)
  if (config) {
    initFilter(config)
    initBox2d(config, node)
    // reactive(config)
    return config
  }
  if (addScriptIfScriptNotExist) {
    const config = node.addScript(scripts.editor.name) as EditorDataScript
    initFilter(config)
    initBox2d(config, node)
    // reactive(config)
    return config
  }
}

export const hasScript = (node: qc.Node | qc.Dom): boolean => {
  return Boolean(node.getScript(scripts.editor.name))
}

function cookAction(action: AstActionItem) {
  const actionConfig = getVpAstActionConfig()
  const method = action.method
  // 由于响应事件的 label 是存储在场景节点的事件脚本的配置中，不区分语言，所以这里需要通过 method 换取到国际化配置文本
  if (actionConfig[method] && actionConfig[method]!.label) {
    action.label = actionConfig[method]!.label
  }
  return action
}

export function cookCondition(condition: AstConditionItem) {
  const vpConditionConf = _.flatten(Object.values(getVpConditionConf()))

  const conditionType = condition.content
  // 由于事件的 conditionLabel 是存储在场景节点的事件脚本的配置中，不区分语言，所以这里需要通过 content（事件类型） 换取到国际化配置文本
  const cur = vpConditionConf.find((item) => item.content === conditionType)
  if (cur && cur.label) {
    condition.conditionLabel = cur.label
  }
  return condition
}

/**
 * vpAst 数据适配器，用于数据转换
 * @param vpAst
 */
function vpAstAdapter(vpAst: { [p: string]: AstEventItem }) {
  _.each(vpAst, (item) => {
    _.each(item.action, (actionItem, index) => {
      item.action[index] = cookAction(actionItem)
    })
    _.each(item.condition, (conditionItem, index) => {
      item.condition[index] = cookCondition(conditionItem)
    })
  })
}

export function getVpAst(node: qc.Node): AstEventItem[] {
  const editorData = getScript(node, false)
  if (editorData && editorData.vpAst) {
    vpAstAdapter(editorData.vpAst)
    // ast是数组，但是持久化在场景树中会变成对象，这里确保他是数组
    return [...Object.values(editorData.vpAst)]
  }
  return []
}

/**
 * 获取节点来源
 */
export function getNodeOrigin(node: qc.Node): number {
  const config = getScript(node) as EditorDataScript
  return config.$data.nodeOrigin
}

export function getTargetNodeAstConfig(
  uuid: string,
  ast: AstEventItem[],
): AstEventItem[] {
  const config: AstEventItem[] = ast.filter((item: AstEventItem) => {
    // 过滤掉空数据
    return (
      item &&
      item.condition &&
      item.condition[0] &&
      item.condition[0].node === uuid
    )
  })
  return _.cloneDeep(config)
}

export const resetScreenAdapter = (
  node: qc.Node,
  resolution: LAYOUT_RESOLUTION,
) => {
  const editorData = getScript(node)!
  if (!editorData.screenAdapter) {
    editorData.screenAdapter = {}
  }
  editorData.screenAdapter[resolution] = {
    horizontal: 'center',
    vertical: 'center',
  }
}

/**
 * 获取当前节点在该分辨率下适配方式
 * @param node 节点
 * @param resolution 分辨率
 * @param addIfNotExist 如果没有是否默认添加
 */
export const getScreenAdapter = (
  node: qc.Node,
  resolution: LAYOUT_RESOLUTION,
  addIfNotExist = true,
): {
  horizontal: string
  vertical: string
} | null => {
  const editorData = getScript(node, addIfNotExist)
  // 部分历史数据，没有 screenAdapter[resolution].horizontal/vertical 需要重置
  if (
    editorData &&
    editorData.screenAdapter &&
    editorData.screenAdapter[resolution] &&
    editorData.screenAdapter[resolution]!.horizontal &&
    editorData.screenAdapter[resolution]!.vertical
  ) {
    return editorData.screenAdapter[resolution]!
  }
  if (addIfNotExist) {
    resetScreenAdapter(node, resolution)
    return editorData!.screenAdapter[resolution]!
  }
  return null
}

export const setScreenAdapter = (
  node: qc.Node,
  resolution: LAYOUT_RESOLUTION,
  value: {
    horizontal: string
    vertical: string
  },
): void => {
  const editorData = getScript(node)!
  if (!editorData.screenAdapter) {
    editorData.screenAdapter = {}
  }
  editorData.screenAdapter[resolution] = value
}

/**
 * 获取节点横竖屏设置值
 * @param node
 * @param prop
 */
export const getUnSplitPropValue = (node: qc.Node, prop: string): boolean => {
  const editorData = getScript(node)

  if (
    editorData &&
    editorData.$data &&
    editorData.$data.unSplitProp &&
    typeof editorData.$data.unSplitProp[prop] !== 'undefined'
  ) {
    return editorData.$data.unSplitProp[prop]!
  }
  return false
}

/**
 * 设置节点横竖屏区分值
 * @param node
 * @param prop
 */
export const setUnSplitPropValue = (
  node: qc.Node,
  prop: string,
  value: boolean,
): void => {
  const editorData = getScript(node)!

  if (editorData.$data && typeof editorData.$data.unSplitProp === 'undefined') {
    editorData.$data.unSplitProp = {}
  }
  editorData.$data.unSplitProp![prop] = value
}
/**
 * 删除节点横竖屏区分值
 * @param node
 * @param prop
 */
export const removeUnSplitPropValue = (node: qc.Node, prop: string): void => {
  const editorData = getScript(node)!

  if (editorData.$data && typeof editorData.$data.unSplitProp === 'undefined') {
    editorData.$data.unSplitProp = {}
  }
  delete editorData.$data.unSplitProp![prop]
}

export const setOldLayoutData = (
  node: qc.Node,
  prop: string,
  value: number,
): void => {
  const editorData = getScript(node)!

  if (
    editorData.$data &&
    typeof editorData.$data.oldLayoutData === 'undefined'
  ) {
    editorData.$data.oldLayoutData = {}
  }
  // @ts-ignore
  editorData.$data.oldLayoutData![prop] = value
}

export const getOldLayoutData = (
  node: qc.Node,
  prop: string,
): number | undefined => {
  const editorData = getScript(node)

  if (
    editorData &&
    editorData.$data &&
    editorData.$data.oldLayoutData &&
    // @ts-ignore
    typeof editorData.$data.oldLayoutData[prop]! !== 'undefined'
  ) {
    // @ts-ignore
    return editorData.$data.oldLayoutData[prop]!
  }
  return undefined
}

export const resetOldLayoutData = (node: qc.Node): void => {
  const editorData = getScript(node)

  if (editorData && editorData.$data && editorData.$data.oldLayoutData) {
    // 清空所有属性
    editorData.$data.oldLayoutData = {}
  }
}
